import { EditorItemType, EditorStarterItem } from '../items/item-type';
import { TrackType, TrackZone } from '../state/types';

export const ZONE_SEPARATOR_HEIGHT = 4;

export const getZoneForItemType = (type: EditorItemType): TrackZone => {
  return type === 'audio' ? 'audio' : 'visual';
};

export const getZoneBoundaryIndex = (tracks: TrackType[]): number => {
  const firstAudioIndex = tracks.findIndex((track) => track.zone === 'audio');
  return firstAudioIndex === -1 ? tracks.length : firstAudioIndex;
};

export const getTracksInZone = (
  tracks: TrackType[],
  zone: TrackZone,
): TrackType[] => {
  return tracks.filter((track) => track.zone === zone);
};

const inferItemType = (
  track: TrackType,
  items: Record<string, EditorStarterItem>,
): EditorItemType | null => {
  for (const itemId of track.items) {
    const item = items[itemId];
    if (item) {
      // captions 아이템 타입은 'captions'이지만 트랙 metadata에서는 'caption'으로 사용
      return item.type === 'captions' ? 'caption' : item.type;
    }
  }
  return null;
};

const inferZone = (
  track: TrackType,
  items: Record<string, EditorStarterItem>,
): TrackZone => {
  if (track.metadata?.type) {
    return getZoneForItemType(track.metadata.type);
  }

  const itemType = inferItemType(track, items);
  if (itemType) {
    return getZoneForItemType(itemType);
  }

  return 'visual';
};

/**
 * metadata가 없는 트랙에 아이템 타입을 기반으로 metadata를 복원한다.
 * 기존 저장 데이터에서 metadata가 누락된 경우를 보완한다.
 */
const inferTrackMetadata = (
  track: TrackType,
  items: Record<string, EditorStarterItem>,
  existingMainTypes: Set<EditorItemType>,
): TrackType['metadata'] => {
  if (track.metadata) return track.metadata;

  const itemType = inferItemType(track, items);
  if (!itemType) return undefined;

  const canBeMainTrack =
    itemType === 'image' ||
    itemType === 'video' ||
    itemType === 'caption' ||
    itemType === 'audio';
  // 같은 타입의 메인 트랙이 이미 존재하면 이 트랙은 메인이 아님
  const isMainTrack = canBeMainTrack && !existingMainTypes.has(itemType);

  if (isMainTrack) {
    existingMainTypes.add(itemType);
  }

  return {
    isMainTrack,
    trackType: isMainTrack ? 'main' : null,
    type: itemType,
  };
};

/**
 * 서버에서 불러온 기존 트랙에 zone 필드가 없을 수 있으므로,
 * zone을 할당하고 visual-first / audio-second 순서로 정렬한다.
 * metadata가 누락된 트랙은 아이템 타입을 기반으로 metadata를 복원한다.
 */
export const normalizeTrackZones = (
  tracks: TrackType[],
  items: Record<string, EditorStarterItem>,
): TrackType[] => {
  // 이미 metadata가 있는 메인 트랙의 타입을 수집
  const existingMainTypes = new Set(
    tracks.filter((t) => t.metadata?.isMainTrack).map((t) => t.metadata!.type),
  );

  const withZoneAndMetadata = tracks.map((track) => {
    const zone = track.zone || inferZone(track, items);
    const metadata = inferTrackMetadata(track, items, existingMainTypes);
    return { ...track, zone, metadata };
  });

  const visualTracks = withZoneAndMetadata.filter((t) => t.zone === 'visual');
  const audioTracks = withZoneAndMetadata.filter((t) => t.zone === 'audio');

  return [...visualTracks, ...audioTracks];
};
